import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Calendar, Users, TrendingUp, DollarSign, UserCheck, BarChart3, Settings, Target, Zap, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';

const useCasesFr = [
{
  role: 'CEO',
  icon: Target,
  color: 'bg-blue-50 border-blue-200',
  iconColor: 'text-blue-600',
  badgeColor: 'bg-blue-100 text-blue-800',
  title: 'Decision strategique et reporting au conseil',
  cases: [
  {
    title: 'Veille concurrentielle en temps reel',
    description: "Utilisez l'IA générative pour synthétiser rapports d'analystes, communiqués de presse et données marché en un brief concurrentiel structuré en quelques minutes.",
    before: '2 jours de travail analytique',
    after: "15 minutes avec l'IA"
  },
  {
    title: "Redaction du narratif pour le conseil d'administration",
    description: "Alimentez un modele d'IA avec vos KPIs trimestriels et generez un narratif executif aligne sur les attentes des investisseurs — un seul passage de relecture au lieu de trois cycles de revision.",
    before: 'Plusieurs cycles de revision',
    after: 'Premier jet pret en 30 min'
  },
  {
    title: 'Screening de cibles M&A',
    description: "Demandez a une IA de croiser les profils de cibles avec vos criteres strategiques et de faire remonter les meilleures options d'une longlist — des semaines de recherche condensees en quelques heures.",
    before: '3 semaines de recherche documentaire',
    after: 'Shortlist classee en 2 heures'
  }]

},
{
  role: 'CFO / DAF',
  icon: DollarSign,
  color: 'bg-emerald-50 border-emerald-200',
  iconColor: 'text-emerald-600',
  badgeColor: 'bg-emerald-100 text-emerald-800',
  title: 'Finance, reporting de gestion & previsions',
  cases: [
  {
    title: 'Automatisation du reporting de gestion',
    description: "Demandez a l'IA de transformer vos exports Excel bruts en rapport de gestion structure avec analyse des ecarts, commentaires rediges et recommandations graphiques.",
    before: '1,5 jour de travail FP&A',
    after: '2 heures avec assistance IA'
  },
  {
    title: 'Extraction des couts contractuels',
    description: "Utilisez l'analyse documentaire IA pour scanner les contrats fournisseurs et extraire conditions de paiement, engagements de volume et clauses penales dans un tableau de bord centralise.",
    before: 'Revue manuelle de 200+ contrats',
    after: "Extraction complete en moins d'une heure"
  },
  {
    title: 'Modelisation budgetaire et scenarios',
    description: "Decrivez vos hypotheses en langage naturel et laissez l'IA les traduire en projections P&L structurees avec scenarios optimiste/central/pessimiste et commentaires pour le conseil.",
    before: 'Journees de modelisation Excel',
    after: 'Deck scenarios en 3 heures'
  }]

},
{
  role: 'DRH / CHRO',
  icon: UserCheck,
  color: 'bg-violet-50 border-violet-200',
  iconColor: 'text-violet-600',
  badgeColor: 'bg-violet-100 text-violet-800',
  title: 'Operations RH, talent & culture',
  cases: [
  {
    title: 'Generation de fiches de poste a grande echelle',
    description: 'Saisissez un brief de poste et obtenez en quelques secondes une offre emploi convaincante, verifiee anti-biais, au langage inclusif, prete a publier sur LinkedIn et votre site carrieres.',
    before: '2-3 heures par fiche de poste',
    after: 'Version finale en 10 minutes'
  },
  {
    title: "Calibration des entretiens d'evaluation",
    description: "Chargez des donnees d'evaluation anonymisees et demandez a l'IA d'identifier les valeurs aberrantes, les tendances par equipe et de generer des points de discussion pour les HRBP avant les comites de revue.",
    before: 'Calibration subjective et incoherente',
    after: 'Insights fondes sur les donnees en 1h'
  },
  {
    title: "Synthese des enquetes d'engagement",
    description: "Alimentez une IA avec les reponses ouvertes de vos enquetes pour extraire themes, tendances de sentiment et priorites d'action — avec un plan d'action segmente par departement.",
    before: 'Semaines de codification manuelle',
    after: 'Rapport thematique en 2 heures'
  }]

},
{
  role: 'Directeur Commercial',
  icon: TrendingUp,
  color: 'bg-orange-50 border-orange-200',
  iconColor: 'text-orange-600',
  badgeColor: 'bg-orange-100 text-orange-800',
  title: 'Sales enablement, pipeline & intelligence client',
  cases: [
  {
    title: 'Prospection hyper-personnalisee a grande echelle',
    description: "Briefez une IA sur les actualites d'un prospect, ses defis sectoriels et votre proposition de valeur — obtenez un email et un message LinkedIn sur-mesure en 60 secondes.",
    before: '30 min de recherche + 20 min de redaction par prospect',
    after: 'Personnalisation complete en 2 minutes'
  },
  {
    title: 'Analyse Win/Loss depuis le CRM',
    description: "Extrayez les notes CRM et l'historique des deals, demandez a l'IA d'identifier les patterns dans les affaires gagnees vs. perdues, et generez un playbook structure pour l'equipe commerciale.",
    before: 'Suppositions qualitatives',
    after: 'Playbook structure en 4 heures'
  },
  {
    title: 'Acceleration des propositions commerciales',
    description: "Alimentez l'IA avec un brief client et vos propositions gagnantes passees pour generer un plan de proposition sur-mesure et un resume executif — concentrez-vous sur la differenciation.",
    before: '1 journee pour structurer une proposition',
    after: 'Plan pret en 45 minutes'
  }]

},
{
  role: 'COO / DG Operations',
  icon: Settings,
  color: 'bg-rose-50 border-rose-200',
  iconColor: 'text-rose-600',
  badgeColor: 'bg-rose-100 text-rose-800',
  title: 'Operations, processus & pilotage de la performance',
  cases: [
  {
    title: 'Cartographie et optimisation des processus',
    description: "Decrivez un workflow operationnel existant en langage naturel et demandez a l'IA d'identifier les goulots d'etranglement, proposer des ameliorations et produire une matrice RACI refondue.",
    before: 'Semaines de consulting processus',
    after: "Premier brief d'optimisation en 2h"
  },
  {
    title: "Evaluation fournisseurs et appels d'offres",
    description: "Utilisez l'IA pour synthetiser les reponses a un appel d'offres selon une matrice de criteres ponderes — produisant un scorecard classe avec commentaires pros/cons pour le comite de pilotage.",
    before: '3-4 jours de travail achats',
    after: 'Scorecard en demi-journee'
  },
  {
    title: 'Automatisation du reporting operationnel',
    description: "Connectez l'IA a vos tableaux de bord operationnels et generez des syntheses hebdomadaires de performance avec commentaires narratifs, alertes et actions recommandees.",
    before: 'Reporting manuel hebdomadaire',
    after: 'Narratif automatise en 30 min'
  }]

}];


const useCasesEn = [
{
  role: 'CEO',
  icon: Target,
  color: 'bg-blue-50 border-blue-200',
  iconColor: 'text-blue-600',
  badgeColor: 'bg-blue-100 text-blue-800',
  title: 'Strategic decision-making & board reporting',
  cases: [
  {
    title: 'Real-time competitive intelligence',
    description: 'Use generative AI to synthesize analyst reports, press releases, and market data into a structured competitive brief in minutes — no more 2-day turnarounds from strategy teams.',
    before: '2 days of analyst work',
    after: '15 minutes with AI'
  },
  {
    title: 'Board deck narrative generation',
    description: 'Feed quarterly KPIs into an AI model and generate a draft executive narrative aligned with investor expectations. Refine in one pass instead of three rounds of revision.',
    before: 'Multiple revision cycles',
    after: 'First draft ready in 30 min'
  },
  {
    title: 'M&A target screening',
    description: 'Prompt an AI to cross-reference target company profiles against strategic criteria and surface top matches from a longlist — compress weeks of screening into hours.',
    before: '3 weeks of desk research',
    after: 'Ranked shortlist in 2 hours'
  }]

},
{
  role: 'CFO',
  icon: DollarSign,
  color: 'bg-emerald-50 border-emerald-200',
  iconColor: 'text-emerald-600',
  badgeColor: 'bg-emerald-100 text-emerald-800',
  title: 'Finance operations, reporting & forecasting',
  cases: [
  {
    title: 'Automated management reporting',
    description: "Instruct an AI to transform raw Excel exports into a structured management report with variance analysis, commentary drafts, and chart recommendations — ready for CFO review.",
    before: '1.5 days of FP&A work',
    after: '2 hours with AI assistance'
  },
  {
    title: 'Contract cost extraction',
    description: 'Use AI document analysis to scan supplier contracts and extract payment terms, volume commitments, and penalty clauses into a centralized dashboard — instantly surface hidden liabilities.',
    before: 'Manual review of 200+ contracts',
    after: 'Full extraction in under an hour'
  },
  {
    title: 'Budget narrative & scenario modelling',
    description: 'Describe your assumptions in plain language and let AI translate them into structured P&L projections with bull/base/bear scenarios and a written commentary for the board.',
    before: 'Days of spreadsheet modelling',
    after: 'Scenario deck in 3 hours'
  }]

},
{
  role: 'CHRO / Head of HR',
  icon: UserCheck,
  color: 'bg-violet-50 border-violet-200',
  iconColor: 'text-violet-600',
  badgeColor: 'bg-violet-100 text-violet-800',
  title: 'People operations, talent & culture',
  cases: [
  {
    title: 'Job description generation at scale',
    description: 'Input a role brief and have AI generate a compelling, bias-checked job description in seconds — consistent tone, inclusive language, ready to post on LinkedIn and your careers page.',
    before: '2-3 hours per JD',
    after: 'Final draft in 10 minutes'
  },
  {
    title: 'Performance review calibration',
    description: 'Upload anonymized review data and have AI flag outliers, identify patterns across teams, and generate calibration talking points for HR business partners before review committees.',
    before: 'Subjective, inconsistent calibration',
    after: 'Data-driven insights in 1 hour'
  },
  {
    title: 'Employee engagement report synthesis',
    description: "Feed open-ended survey responses into an AI to extract themes, sentiment trends, and priority action areas — with a draft action plan segmented by department.",
    before: 'Weeks of manual coding',
    after: 'Thematic report in 2 hours'
  }]

},
{
  role: 'CSO / Head of Sales',
  icon: TrendingUp,
  color: 'bg-orange-50 border-orange-200',
  iconColor: 'text-orange-600',
  badgeColor: 'bg-orange-100 text-orange-800',
  title: 'Sales enablement, pipeline & client intelligence',
  cases: [
  {
    title: 'Hyper-personalised outreach at scale',
    description: "Brief an AI on a prospect's recent news, sector challenges, and your value proposition — get a tailored outreach email and LinkedIn message in 60 seconds, at the pace of your full pipeline.",
    before: '30 min research + 20 min writing per prospect',
    after: 'Full personalisation in 2 minutes'
  },
  {
    title: 'Win/loss analysis from CRM data',
    description: 'Extract CRM notes and deal histories, prompt AI to identify common patterns in won vs. lost deals, and generate a structured playbook for the sales team.',
    before: 'Qualitative guesswork',
    after: 'Structured playbook in 4 hours'
  },
  {
    title: 'Proposal and pitch deck acceleration',
    description: 'Feed a client brief and previous winning proposals into AI to generate a tailored proposal outline and executive summary — let the team focus on differentiation, not formatting.',
    before: '1 day to structure a proposal',
    after: 'Ready outline in 45 minutes'
  }]

},
{
  role: 'COO',
  icon: Settings,
  color: 'bg-rose-50 border-rose-200',
  iconColor: 'text-rose-600',
  badgeColor: 'bg-rose-100 text-rose-800',
  title: 'Operations, processes & performance management',
  cases: [
  {
    title: 'Process mapping and optimization',
    description: 'Describe an existing operational workflow in natural language and ask AI to identify bottlenecks, suggest process improvements, and produce a redesigned RACI matrix.',
    before: 'Weeks of process consulting',
    after: 'First optimization brief in 2 hours'
  },
  {
    title: 'Vendor and supplier assessment',
    description: 'Use AI to synthesize RFP responses from multiple vendors against a weighted criteria matrix — producing a ranked scorecard with pros/cons commentary ready for steering committee.',
    before: '3-4 days of procurement work',
    after: 'Ranked scorecard in half a day'
  },
  {
    title: 'Operational reporting automation',
    description: 'Connect AI to your operational dashboards and generate weekly performance summaries with narrative commentary, exception flags, and recommended actions.',
    before: 'Manual weekly reporting',
    after: 'Automated narrative in 30 min'
  }]

}];


const bootcampModulesEn = [
{ week: 'Weeks 1-2', title: 'AI Fundamentals for Executives', desc: 'Landscape of generative AI, key model families, capabilities and limitations. Understanding what AI can and cannot do for your function.' },
{ week: 'Weeks 3-4', title: 'Prompt Engineering for Business Leaders', desc: 'How to craft effective prompts to get reliable, high-quality outputs. Hands-on practice with your real documents and datasets.' },
{ week: 'Weeks 5-6', title: 'Role-Specific Use Cases (Workshop)', desc: 'Deep-dive into the 3 highest-impact AI use cases for your function. Live demonstrations and guided exercises on your own operational context.' },
{ week: 'Weeks 7-8', title: 'AI Integration & Change Management', desc: 'How to deploy AI tools in your teams responsibly: governance frameworks, data security, employee adoption, and measuring ROI.' },
{ week: 'Weeks 9-10', title: 'AI-Augmented Decision Making', desc: 'Advanced synthesis: using AI as a strategic thought partner for board reporting, scenario planning, and executive communication.' },
{ week: 'Weeks 11-12', title: 'Capstone: Your Personal AI Roadmap', desc: 'Each participant builds a concrete 90-day AI adoption plan for their function, presented and peer-reviewed in a final session.' }];


const bootcampModulesFr = [
{ week: 'Semaines 1-2', title: "Fondamentaux de l'IA pour dirigeants", desc: "Panorama de l'IA generative, principales familles de modeles, capacites et limites. Comprendre ce que l'IA peut et ne peut pas faire pour votre fonction." },
{ week: 'Semaines 3-4', title: 'Prompt Engineering pour decideurs', desc: 'Comment formuler des instructions efficaces pour obtenir des resultats fiables et de haute qualite. Pratique intensive avec vos propres documents et donnees.' },
{ week: 'Semaines 5-6', title: "Cas d'usage metier (Workshop)", desc: "Exploration approfondie des 3 cas d'usage IA a plus fort impact pour votre fonction. Demonstrations live et exercices guides dans votre contexte operationnel reel." },
{ week: 'Semaines 7-8', title: 'Integration IA & conduite du changement', desc: 'Comment deployer les outils IA dans vos equipes de facon responsable : gouvernance, securite des donnees, adoption par les collaborateurs et mesure du ROI.' },
{ week: 'Semaines 9-10', title: "Prise de decision augmentee par l'IA", desc: "Synthese avancee : utiliser l'IA comme partenaire strategique pour le reporting au conseil, la planification par scenarios et la communication executive." },
{ week: 'Semaines 11-12', title: 'Capstone : votre feuille de route IA personnelle', desc: "Chaque participant construit un plan d'adoption IA concret sur 90 jours pour sa fonction, presente et discute en session finale." }];


export const GeneralManagementPage = () => {
  const { language } = useLanguage();

  const cases = language === 'fr' ? useCasesFr : useCasesEn;
  const modules = language === 'fr' ? bootcampModulesFr : bootcampModulesEn;

  const seoTitle = language === 'fr' ?
  'Formation IA pour Dirigeants | Gaius' :
  'AI Training for C-Suite Executives | Gaius';

  const seoDescription = language === 'fr' ?
  'Bootcamp IA sur-mesure pour CEO, CFO, DRH, Directeurs Commerciaux et COO. Cas usage concrets, format bimensuel sur 2 a 3 mois.' :
  'Tailored AI bootcamp for CEOs, CFOs, CHROs, Heads of Sales and COOs. Real use cases, biweekly sessions over 2 to 3 months.';

  const whyItemsFr = [
  { icon: BarChart3, title: 'ROI immediat', desc: 'Chaque session delivre des techniques et outils directement applicables le lendemain matin dans votre agenda de dirigeant.' },
  { icon: Users, title: 'Pedagogie par les pairs', desc: 'Les sessions reunissent des profils CXO complementaires. Les echanges inter-fonctions sont aussi formateurs que le contenu lui-meme.' },
  { icon: Target, title: 'Sur-mesure, pas generique', desc: "Le programme est adapte au secteur, a la taille et au contexte de votre organisation. Pas de cas d'usage fictifs — uniquement votre realite operationnelle." },
  { icon: CheckCircle, title: 'Formateurs praticiens', desc: "Nos formateurs sont des professionnels qui utilisent l'IA dans leur propre pratique quotidienne — pas des theoriciens de la technologie." }];

  const whyItemsEn = [
  { icon: BarChart3, title: 'Immediate ROI', desc: 'Each session delivers techniques and tools directly applicable the next morning in your executive agenda.' },
  { icon: Users, title: 'Peer-to-peer learning', desc: 'Sessions bring together complementary CXO profiles. Cross-functional exchanges are as formative as the content itself.' },
  { icon: Target, title: 'Tailored, not generic', desc: 'The programme is adapted to your sector, size and organisational context. No fictitious use cases — only your operational reality.' },
  { icon: CheckCircle, title: 'Practitioner trainers', desc: 'Our trainers are professionals who use AI in their own daily practice — not technology theorists.' }];


  const deliverablesFr = [
  '6 sessions live de 2h avec des experts IA et management',
  'Supports de cours et bibliotheque de prompts dediee a votre fonction',
  'Acces a un espace de travail IA partage pendant le bootcamp',
  'Coaching individuel entre les sessions (1h par participant)',
  'Feuille de route IA personnalisee pour votre fonction (livrable final)',
  'Certificat de formation Gaius — Direction Generale & IA'];

  const deliverablesEn = [
  '6 live sessions of 2h with AI and management experts',
  'Course materials and a prompt library dedicated to your function',
  'Access to a shared AI workspace during the bootcamp',
  'Individual coaching between sessions (1h per participant)',
  'Personalised AI roadmap for your function (final deliverable)',
  'Gaius Training Certificate — General Management & AI'];


  const whyItems = language === 'fr' ? whyItemsFr : whyItemsEn;
  const deliverables = language === 'fr' ? deliverablesFr : deliverablesEn;

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={language === 'fr' ?
        'formation ia dirigeants, ia ceo, ia cfo, ia drh, ia directeur commercial, bootcamp ia management' :
        'ai training executives, ai ceo, ai cfo, ai chro, ai head of sales, ai bootcamp management'}
        canonicalUrl={language === 'fr' ? 'https://gaius-tech.com/formation-ia-direction-generale' : 'https://gaius-tech.com/ai-training-general-management'}
        alternateUrlFr="https://gaius-tech.com/formation-ia-direction-generale"
        alternateUrlEn="https://gaius-tech.com/ai-training-general-management" />
      

      {/* Hero */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-secondary via-background to-secondary/50 relative overflow-hidden">
        <div className="absolute top-20 right-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Zap className="w-4 h-4 text-primary mr-2" />
              <span className="text-sm font-medium text-primary">
                {language === 'fr' ? 'Programme Bootcamp · 2 a 3 mois' : 'Bootcamp Programme · 2 to 3 months'}
              </span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6 leading-tight">
              {language === 'fr' ?
              <span>{"L'IA au service de la "}<span className="text-primary">Direction Generale</span></span> :
              <span>{'AI for '}<span className="text-primary">General Management</span></span>}
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed mb-8">
              {language === 'fr' ?
              "Un programme de formation intensif concu pour les decideurs — CEO, CFO, DRH, Directeurs Commerciaux, COO. Des cas d'usage reels, des resultats mesurables, et une adoption durable de l'IA dans vos processus de direction." :
              'An intensive training programme designed for business leaders — CEOs, CFOs, CHROs, Heads of Sales, and COOs. Real use cases, measurable outcomes, and lasting AI adoption in your management processes.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-lg h-14 px-8">
                <Link to="/contact" className="flex items-center">
                  {language === 'fr' ? 'Demander une presentation' : 'Request a Presentation'}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="text-lg h-14 px-8 border-2">
              <Link to={language === 'fr' ? '/formation-ia-avocat' : '/legal-ai-training-lawyers'}>
                  {language === 'fr' ? 'Voir toutes nos formations' : 'See All Training Programs'}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Key stats */}
      <section className="py-12 bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
            { value: '5', label: language === 'fr' ? 'Profils CXO couverts' : 'CXO Profiles Covered', icon: Users },
            { value: '2-3', label: language === 'fr' ? 'Mois de bootcamp' : 'Months Bootcamp', icon: Calendar },
            { value: '15+', label: language === 'fr' ? "Cas d'usage concrets" : 'Concrete Use Cases', icon: Target },
            { value: '6', label: language === 'fr' ? 'Sessions bimensuelles' : 'Biweekly Sessions', icon: Clock }].
            map((stat, i) =>
            <div key={i} className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-3">
                  <stat.icon className="w-6 h-6 text-primary" />
                </div>
                <div className="text-3xl font-bold text-foreground">{stat.value}</div>
                <div className="text-sm text-muted-foreground mt-1">{stat.label}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Use cases per role */}
      <section className="py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              {language === 'fr' ? "Cas d'usage concrets par fonction" : 'Concrete Use Cases by Function'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {language === 'fr' ?
              "Chaque module est construit autour de cas d'usage operationnels reels, immediatement applicables dans votre quotidien de dirigeant." :
              'Each module is built around real operational use cases, immediately applicable to your daily leadership work.'}
            </p>
          </div>

          <div className="space-y-16">
            {cases.map((profile, index) =>
            <div key={index}>
                <div className="flex items-center gap-4 mb-8">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center border-2 ${profile.color}`}>
                    <profile.icon className={`w-7 h-7 ${profile.iconColor}`} />
                  </div>
                  <div>
                    <Badge className={`mb-1 ${profile.badgeColor} border-0`}>{profile.role}</Badge>
                    <h3 className="text-2xl font-bold text-foreground">{profile.title}</h3>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {profile.cases.map((uc, ucIndex) =>
                <Card key={ucIndex} className={`border-2 ${profile.color} bg-white hover:shadow-lg transition-shadow`}>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base font-bold text-foreground leading-snug">
                          {uc.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                          {uc.description}
                        </CardDescription>
                        <div className="space-y-2 pt-2 border-t border-border">
                          <div className="flex items-start gap-2">
                            <span className="text-xs font-semibold text-destructive mt-0.5 shrink-0">
                              {language === 'fr' ? 'Avant :' : 'Before:'}
                            </span>
                            <span className="text-xs text-muted-foreground">{uc.before}</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
                            <span className="text-xs font-medium text-foreground">{uc.after}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Bootcamp structure */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              {language === 'fr' ? 'Structure du bootcamp' : 'Bootcamp Structure'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {language === 'fr' ?
              "6 sessions bimensuelles de 2h sur 2 a 3 mois. Un format adapte aux agendas de dirigeants — intense, efficace, et directement operationnel." :
              '6 biweekly sessions of 2 hours over 2 to 3 months. A format designed for executive schedules — intensive, effective, and immediately operational.'}
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-primary/20 hidden md:block" />
            <div className="space-y-8">
              {modules.map((mod, index) =>
              <div key={index} className="relative flex gap-8 items-start">
                  <div className="relative z-10 hidden md:flex flex-col items-center">
                    <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm shrink-0">
                      {index + 1}
                    </div>
                  </div>
                  <Card className="flex-1 border border-border hover:shadow-md transition-shadow">
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-3 mb-2">
                        <Badge variant="outline" className="text-primary border-primary font-medium">
                          <Calendar className="w-3 h-3 mr-1" />
                          {mod.week}
                        </Badge>
                      </div>
                      <CardTitle className="text-lg text-foreground">{mod.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                        {mod.desc}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Why this programme */}
      <section className="py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-foreground mb-6">
                {language === 'fr' ? 'Pourquoi ce programme ?' : 'Why this programme?'}
              </h2>
              <div className="space-y-6">
                {whyItems.map((item, i) =>
                <div key={i} className="flex gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <item.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground mb-1">{item.title}</h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-border p-8 shadow-lg">
              <h3 className="text-xl font-bold text-foreground mb-6">
                {language === 'fr' ? 'Ce que vous obtenez' : 'What you get'}
              </h3>
              <ul className="space-y-4">
                {deliverables.map((item, i) =>
                <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <span className="text-sm text-muted-foreground">{item}</span>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-primary-foreground mb-6">
            {language === 'fr' ?
            'Pret a engager votre comite de direction ?' :
            'Ready to engage your executive committee?'}
          </h2>
          <p className="text-xl text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
            {language === 'fr' ?
            'Contactez-nous pour une presentation sur-mesure du programme et un devis adapte a votre organisation.' :
            'Contact us for a tailored programme presentation and a quote adapted to your organisation.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="secondary" className="text-lg px-8 h-14">
              <Link to="/contact" className="flex items-center space-x-2">
                <span>{language === 'fr' ? 'Demander une presentation' : 'Request a Presentation'}</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-lg px-8 h-14 text-primary-foreground border-primary-foreground hover:bg-primary-foreground hover:text-primary">
              <Link to={language === 'fr' ? '/formation-ia-avocat' : '/legal-ai-training-lawyers'} className="text-primary">
                {language === 'fr' ? 'Toutes nos formations' : 'All Training Programs'}
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>);

};